import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Space, message, Modal } from 'antd';
import { UserOutlined, BookOutlined, DollarOutlined, ExclamationCircleOutlined, PlusOutlined, CameraOutlined } from '@ant-design/icons';
import { adminAPI, registrationAPI } from '../../services/api';
import { DashboardStats, WithdrawalRequest, CourseChangeRequest } from '../../types';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { confirm } = Modal;

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [changeRequests, setChangeRequests] = useState<CourseChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, withdrawData, changeData] = await Promise.all([
        adminAPI.getDashboard(),
        registrationAPI.getWithdrawalRequests(),
        registrationAPI.getChangeRequests(),
      ]);
      
      setStats(statsData);
      setWithdrawalRequests(withdrawData);
      setChangeRequests(changeData);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessWithdrawal = async (id: number, status: 'approved' | 'rejected', notes?: string) => {
    try {
      await registrationAPI.processWithdrawalRequest(id, status, notes);
      message.success('处理成功');
      fetchData();
    } catch (error) {
      message.error('处理失败');
    }
  };

  const handleProcessChange = async (id: number, status: 'approved' | 'rejected', notes?: string) => {
    try {
      await registrationAPI.processChangeRequest(id, status, notes);
      message.success('处理成功');
      fetchData();
    } catch (error) {
      message.error('处理失败');
    }
  };

  const showProcessModal = (record: WithdrawalRequest | CourseChangeRequest, type: 'withdrawal' | 'change') => {
    let notes = '';
    const courseName = type === 'withdrawal' 
      ? (record as WithdrawalRequest).courseName 
      : (record as CourseChangeRequest).originalCourseName;
    
    confirm({
      title: `处理${type === 'withdrawal' ? '退课' : '变更'}申请`,
      content: (
        <div>
          <p>用户：{record.username} ({record.realName})</p>
          <p>课程：{courseName}</p>
          <p>原因：{record.reason}</p>
          {type === 'withdrawal' && (record as WithdrawalRequest).refundAmount && (
            <p>退款金额：¥{(record as WithdrawalRequest).refundAmount}</p>
          )}
          <textarea
            placeholder="处理备注"
            style={{ width: '100%', marginTop: 16, padding: 8 }}
            onChange={(e) => (notes = e.target.value)}
          />
        </div>
      ),
      onOk() {
        if (type === 'withdrawal') {
          handleProcessWithdrawal(record.id, 'approved', notes);
        } else {
          handleProcessChange(record.id, 'approved', notes);
        }
      },
      onCancel() {
        if (type === 'withdrawal') {
          handleProcessWithdrawal(record.id, 'rejected', notes);
        } else {
          handleProcessChange(record.id, 'rejected', notes);
        }
      },
    });
  };

  const withdrawalColumns = [
    {
      title: '用户信息',
      key: 'user',
      render: (_: any, record: WithdrawalRequest) => (
        <div>
          <div>{record.username}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.realName}</div>
        </div>
      ),
    },
    {
      title: '课程',
      dataIndex: 'courseName',
    },
    {
      title: '退款金额',
      dataIndex: 'refundAmount',
      render: (amount: number) => `¥${amount}`,
    },
    {
      title: '原因',
      dataIndex: 'reason',
      ellipsis: true,
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      render: (time: string) => dayjs(time).format('MM-DD HH:mm'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status: string) => {
        const colors = { pending: 'orange', approved: 'green', rejected: 'red' };
        const texts = { pending: '待处理', approved: '已批准', rejected: '已拒绝' };
        return <Tag color={colors[status as keyof typeof colors]}>{texts[status as keyof typeof texts]}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: WithdrawalRequest) => (
        <Space>
          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              onClick={() => showProcessModal(record, 'withdrawal')}
            >
              处理
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const changeColumns = [
    {
      title: '用户信息',
      key: 'user',
      render: (_: any, record: CourseChangeRequest) => (
        <div>
          <div>{record.username}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.realName}</div>
        </div>
      ),
    },
    {
      title: '原课程',
      dataIndex: 'originalCourseName',
    },
    {
      title: '目标课程',
      dataIndex: 'targetCourseName',
    },
    {
      title: '原因',
      dataIndex: 'reason',
      ellipsis: true,
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      render: (time: string) => dayjs(time).format('MM-DD HH:mm'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status: string) => {
        const colors = { pending: 'orange', approved: 'green', rejected: 'red' };
        const texts = { pending: '待处理', approved: '已批准', rejected: '已拒绝' };
        return <Tag color={colors[status as keyof typeof colors]}>{texts[status as keyof typeof texts]}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CourseChangeRequest) => (
        <Space>
          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              onClick={() => showProcessModal(record, 'change')}
            >
              处理
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>管理后台</h1>
        <Space>
          <Button 
            type="primary" 
            icon={<CameraOutlined />}
            onClick={() => navigate('/admin/payment-review')}
          >
            支付审核
          </Button>
          <Button 
            type="primary" 
            icon={<DollarOutlined />}
            onClick={() => navigate('/admin/orders')}
          >
            订单管理
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/courses')}
          >
            课程管理
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总用户数"
              value={stats?.userStats.totalUsers || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总课程数"
              value={stats?.courseStats.totalCourses || 0}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总报名数"
              value={stats?.registrationStats.totalRegistrations || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总收入"
              value={stats?.revenueStats.totalRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="今日收入"
              value={stats?.revenueStats.revenueToday || 0}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="本周收入"
              value={stats?.revenueStats.revenueThisWeek || 0}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="本月收入"
              value={stats?.revenueStats.revenueThisMonth || 0}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="待处理申请"
              value={(stats?.pendingRequests.pendingWithdrawals || 0) + (stats?.pendingRequests.pendingChanges || 0)}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 待处理申请 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="退课申请" extra={<span>共 {withdrawalRequests.filter(r => r.status === 'pending').length} 条</span>}>
            <Table
              columns={withdrawalColumns}
              dataSource={withdrawalRequests.filter(r => r.status === 'pending')}
              pagination={false}
              scroll={{ x: 600 }}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="课程变更申请" extra={<span>共 {changeRequests.filter(r => r.status === 'pending').length} 条</span>}>
            <Table
              columns={changeColumns}
              dataSource={changeRequests.filter(r => r.status === 'pending')}
              pagination={false}
              scroll={{ x: 600 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;